import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

// Tailwind class sets per accent color. Must be complete strings (no dynamic
// interpolation) so Tailwind's JIT scanner can detect them at build time.
const ACCENT_CLASSES = {
  sky: {
    border: "border-sky-500/60",
    bg:     "bg-sky-600/20",
    tag:    "text-sky-400",
  },
  amber: {
    border: "border-amber-500/60",
    bg:     "bg-amber-600/20",
    tag:    "text-amber-400",
  },
  red: {
    border: "border-red-500/60",
    bg:     "bg-red-600/20",
    tag:    "text-red-400",
  },
  violet: {
    border: "border-violet-500/60",
    bg:     "bg-violet-600/20",
    tag:    "text-violet-400",
  },
  slate: {
    border: "border-slate-500/40",
    bg:     "bg-slate-700/60",
    tag:    "text-slate-400",
  },
};

/**
 * Unified in-game notification stack. Rendered via createPortal at document.body
 * so it sits above all game UI without fighting z-index hierarchies.
 *
 * Props:
 *   notifications — array of { id, icon, accent, teamLabel, title, body, dismissMs }
 *   onDismiss(id) — called when user clicks X or dismissMs elapses
 */
export default function GameNotificationStack({ notifications, onDismiss }) {
  if (!notifications.length) return null;

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[9000]
                 flex flex-col items-center gap-2 w-full max-w-[440px] px-3
                 pointer-events-none"
    >
      <AnimatePresence initial={false}>
        {notifications.map((n) => {
          const a = ACCENT_CLASSES[n.accent] ?? ACCENT_CLASSES.slate;
          return (
            <motion.div
              key={n.id}
              layout
              initial={{ opacity: 0, y: -14, scale: 0.96 }}
              animate={{ opacity: 1, y: 0,   scale: 1    }}
              exit={{    opacity: 0, y: -8,   scale: 0.96 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className={`pointer-events-auto w-full flex items-start gap-2.5
                          px-3 py-2 rounded-lg border ${a.border} ${a.bg}
                          backdrop-blur-sm shadow-xl shadow-black/40`}
            >
              {/* icon */}
              <span className="text-base mt-0.5 flex-shrink-0 select-none">
                {n.icon}
              </span>

              {/* text content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5 flex-wrap">
                  <span className={`text-[10px] font-black uppercase tracking-widest ${a.tag} flex-shrink-0`}>
                    {n.teamLabel}
                  </span>
                  {n.title && (
                    <span className="text-[10px] font-semibold text-slate-400 truncate">
                      {n.title}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-200 leading-snug mt-0.5 line-clamp-2">
                  {n.body}
                </p>
              </div>

              {/* dismiss button — always visible so players can clear immediately */}
              <button
                onClick={() => onDismiss(n.id)}
                className="flex-shrink-0 text-slate-500 hover:text-slate-300
                           transition-colors mt-0.5 pointer-events-auto"
                title="Dismiss"
              >
                <X size={11} />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
